import type { UsDiscoveryMarket } from './marketClient';
import type { UsPositionRow } from './types';
import { usEvidenceNow } from './timeEvidence';
import { estimateUsOrderEconomics } from './ticketModel';
import type { UsBrowserBoardQuote, UsBrowserLineQuote } from '@/stores/usBoardQuotes';

/** Display evidence only. Browser quotes never become ticket/rule authority. */
export type UsPositionMarket = UsDiscoveryMarket | UsBrowserBoardQuote | UsBrowserLineQuote;

const amount = (text: string | null | undefined): number | null => {
  if (typeof text !== 'string' || !/^-?(?:0|[1-9]\d*)(?:\.\d{1,12})?$/.test(text)) return null;
  const value = Number(text);
  // Numbers are only a display boundary; exact source strings remain on the row.
  return Number.isFinite(value) && Math.abs(value) * 100 <= Number.MAX_SAFE_INTEGER ? value : null;
};

/** Same midpoint and entry-plus-opening-fees convention as international Poly.
 * Only this exact market/outcome and an unexpired source book can supply a mark.
 * No order/availability authority, network work or alternate-venue fallback. */
export function usPositionEconomics(row: UsPositionRow, market?: UsPositionMarket | null, now = Date.now()) {
  const quantity = amount(row.heldQuantity);
  const accounting = row.accounting;
  const basis = amount(accounting?.costBasisUsd);
  const average = amount(accounting?.averagePriceUsd);
  const openingFees = amount(accounting?.openingFeesUsd);
  const known = row.quantityBasis === 'g1_ledger' && row.reasons.length === 0 && quantity !== null && quantity > 0;
  const averageCents = known && average !== null && average >= 0 && average <= 1 ? average * 100 : null;
  const costDollars = known && basis !== null && openingFees !== null ? basis + openingFees : null;
  let mark: number | null = null;
  if (market?.marketSlug === row.target.marketSlug) {
    const book = market.book;
    const current = 'currentAt' in market ? market.currentAt() : market.book.state === 'current'
      && market.book.expiresAtMs !== null && usEvidenceNow(market.timeEvidence, now) < market.book.expiresAtMs
      && usEvidenceNow(market.timeEvidence, now) < market.classification.expiresAtMs;
    if (current) {
      const bid = amount(row.target.outcome === 'yes' ? book.yesBidUsd : book.noBidUsd);
      const ask = amount(row.target.outcome === 'yes' ? book.yesAskUsd : book.noAskUsd);
      if (bid !== null && ask !== null && bid >= 0 && ask <= 1 && bid <= ask) mark = (bid + ask) / 2;
    }
  }
  const estimate = known && mark !== null ? estimateUsOrderEconomics({ action: 'sell',
    quantity: row.heldQuantity!, outcomePrice: mark.toFixed(12).replace(/0+$/, '').replace(/\.$/, '') }) : null;
  const valueDollars = known && mark !== null ? quantity * mark : null;
  const cashOutDollars = valueDollars !== null && estimate ? valueDollars - Number(estimate.feeCents) / 100 : null;
  const pnlDollars = costDollars !== null && cashOutDollars !== null ? cashOutDollars - costDollars : null;
  const reason = !known ? 'Holding quantity is not established.' : basis === null || averageCents === null
    ? 'Entry cost is unavailable from complete recorded history.' : openingFees === null
      ? 'The remaining position’s entry fees are unavailable.' : mark === null
        ? 'A current quote for this exact market and outcome is unavailable.' : !estimate
          ? 'A closing-fee estimate is unavailable.' : null;
  return { averageCents, entryBasisDollars: known ? basis : null, openingFeesDollars: known ? openingFees : null, costDollars,
    nowCents: mark === null ? null : mark * 100, valueDollars, cashOutDollars, pnlDollars,
    ifWinDollars: known && costDollars !== null ? quantity - costDollars : null,
    grossPayoutDollars: known ? quantity : null, complete: pnlDollars !== null, reason };
}
